/**
 * useSectionOrder — shared hook for LP dynamic section ordering.
 * Fetches saved section order from CMS, merges with defaults, returns ordered list.
 *
 * Production hardening (2026-05-23): the public CMS endpoint occasionally
 * returns 5xx (cold cache miss + Supabase connection pool pressure). The
 * legacy `r.ok ? r.json() : []` swallowed those failures, so the
 * BookingConfirmed page silently fell back to its hardcoded default
 * section order — which made it look like "the CMS layout isn't
 * reflecting on production". We now retry transient failures with a
 * small backoff so the hook gives the saved layout a chance to land
 * before the user notices.
 */
import { useState, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const MAX_ATTEMPTS = 4;
// 250ms, 600ms, 1.4s, 2.5s — bounded so a flaky CDN window doesn't lock
// the page on defaults for long enough for the user to bail.
const RETRY_DELAYS_MS = [250, 600, 1400, 2500];

async function fetchPublicCmsWithRetry() {
  let lastErr = null;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    try {
      const res = await fetch(`${API}/api/admin/cms/content/public`);
      // 2xx → return parsed body. 4xx (other than 408/429) is a real
      // client/data error and won't get better with a retry, so bail.
      if (res.ok) return await res.json();
      if (res.status < 500 && res.status !== 408 && res.status !== 429) {
        return [];
      }
      lastErr = new Error(`CMS fetch ${res.status}`);
    } catch (e) {
      // Network errors (DNS, CORS preflight, abort) all land here.
      lastErr = e;
    }
    // Don't sleep after the final attempt.
    if (attempt < MAX_ATTEMPTS - 1) {
      await new Promise(r => setTimeout(r, RETRY_DELAYS_MS[attempt]));
    }
  }
  // All attempts exhausted — return [] so the consumer falls back to
  // its provided defaults instead of crashing.
  // eslint-disable-next-line no-console
  console.warn('useSectionOrder: CMS fetch failed after retries', lastErr);
  return [];
}

export function useSectionOrder(layoutSection, defaultSections) {
  const [sectionOrder, setSectionOrder] = useState(defaultSections);
  // Tracks whether the saved CMS layout has been fetched & merged yet.
  // Consumers that toggle section visibility (e.g. BookingConfirmed) MUST
  // gate the filter on this flag — otherwise the user sees a brief flash
  // of all sections rendered visible (the defaults) before the saved
  // layout lands and the hidden sections collapse. That flash made it
  // look like the admin's "hide" toggles weren't respected at all.
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchPublicCmsWithRetry();
      if (cancelled) return;
      const layout = data.find(item =>
        item.section === layoutSection && item.metadata?.key === 'section_order'
      );
      if (layout?.metadata?.sections) {
        const saved = layout.metadata.sections;
        const merged = saved.map(s => {
          const def = defaultSections.find(d => d.id === s.id);
          return def ? { ...def, visible: s.visible !== false } : null;
        }).filter(Boolean);
        // Add any new default sections not yet in saved order
        defaultSections.forEach(def => {
          if (!merged.find(m => m.id === def.id)) merged.push(def);
        });
        setSectionOrder(merged);
      }
      // Always flip the flag — even when the fetch returned no saved
      // layout (admin never customised) we want consumers to render the
      // defaults rather than wait forever.
      setIsLoaded(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutSection]);

  // Attach isLoaded as a non-enumerable-ish property so existing
  // consumers that treat the return value as an array still work.
  sectionOrder.isLoaded = isLoaded;
  return sectionOrder;
}
